/**
 * Class Details Hook
 * Fetch class details and enrollments for a specific class
 */

import { useState, useCallback } from 'react';
import { apiGet, ApiError } from '../services/api';
import type { Class, Enrollment } from '@cloak-next/shared';

interface UseClassDetailsResult {
  classData: Class | null;
  enrollments: Enrollment[];
  loading: boolean;
  error: string | null;
  fetchClassDetails: (className: string) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching class details with enrollments
 */
export function useClassDetails(className: string): UseClassDetailsResult {
  const [classData, setClassData] = useState<Class | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch class details and enrollments
   */
  const fetchClassDetails = useCallback(async (name: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both class details and enrollments in parallel
      const [classResponse, enrollmentsResponse] = await Promise.all([
        apiGet<{ success: boolean; data: Class }>(`/classes/${encodeURIComponent(name)}`),
        apiGet<{ success: boolean; data: Enrollment[] }>(`/classes/${encodeURIComponent(name)}/students`),
      ]);

      setClassData(classResponse.data);
      setEnrollments(enrollmentsResponse.data || []);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch class details';
      setError(message);
      console.error('Failed to fetch class details:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refetch with current className
   */
  const refetch = useCallback(async () => {
    if (className) {
      await fetchClassDetails(className);
    }
  }, [className, fetchClassDetails]);

  return {
    classData,
    enrollments,
    loading,
    error,
    fetchClassDetails,
    refetch,
  };
}
